// 套装口径：照原系统 src/lib/order-bundle-lines.ts 与 orders/[id]/OrderDetailClient.tsx 的
// buildShipmentLineGroups / getShipmentBundleQuantity / getBundleShippedQuantity，不照高保真稿的示意数字。
// 发货批次的 ShipmentLine 挂在组件订单行上，各有 quantity + lossQuantity：
//   套装按套显示（套数 = 各「按套计」组件 floor(发货 / 每套用量) 的最小值），破损/补耗按组件逐行、单位「个」。
import { num } from '@/lib/format';
import type { BundleLine, FullShipment, FullShipmentLine, ShipmentOrder } from './types';

type RuleShape = Pick<BundleLine, 'bundleQuantityMode' | 'bundleUnitQuantity' | 'bundleSetsPerGroup' | 'bundleQuantity' | 'sourceBundleName' | 'quantity'>;

export function modeOf(l: Pick<BundleLine, 'bundleQuantityMode'>): 'PER_SET' | 'PER_GROUP' {
  return l.bundleQuantityMode === 'PER_GROUP' ? 'PER_GROUP' : 'PER_SET';
}

export function isBundleLine(l?: Pick<BundleLine, 'bundleId' | 'bundleInstanceId' | 'sourceBundleName'> | null) {
  return !!(l && (l.bundleId || l.bundleInstanceId || l.sourceBundleName));
}

export function bundleName(l?: Pick<BundleLine, 'sourceBundleName' | 'bundle'> | null) {
  if (l?.bundle?.name?.trim()) return l.bundle.name.trim();
  return String(l?.sourceBundleName || '产品套装').replace(/\s+x\s+\d+(?:\.\d+)?\s*套\s*$/, '').trim() || '产品套装';
}

/** 订购套数 */
export function setQuantity(l: RuleShape) {
  const stored = num(l.bundleQuantity);
  if (stored > 0) return stored;
  const m = l.sourceBundleName?.match(/\s+x\s*(\d+(?:\.\d+)?)\s*套\s*$/);
  return m ? Number(m[1]) : 1;
}

/** 每套（或每组）用量 */
export function unitQuantity(l: RuleShape) {
  const stored = num(l.bundleUnitQuantity);
  if (stored > 0) return stored;
  const sets = setQuantity(l);
  return sets > 0 ? num(l.quantity) / sets : num(l.quantity);
}

export function setsPerGroup(l: RuleShape) {
  const v = num(l.bundleSetsPerGroup);
  return v > 0 ? v : 0;
}

/** 用量规则文案，与原系统 formatBundleQuantityRule 一致 */
export function ruleText(l: RuleShape) {
  const u = unitQuantity(l);
  if (modeOf(l) !== 'PER_GROUP') return `每套 ${fmt(u)} 个`;
  return `每 ${fmt(setsPerGroup(l))} 套使用 ${fmt(u)} 个`;
}

function fmt(n: number) {
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(3)));
}

function groupKey(l: BundleLine, fallback: string) {
  if (l.bundleInstanceId) return `bundle-${l.bundleInstanceId}`;
  return `bundle-legacy-${l.bundleId || bundleName(l)}-${fallback}`;
}

/* ------------------------------ 批次明细分组 ------------------------------ */

export type ShipLineGroup =
  | { kind: 'single'; key: string; line: FullShipmentLine }
  | { kind: 'bundle'; key: string; name: string; code?: string; lines: FullShipmentLine[]; sets: number | null; ship: number; loss: number };

/** 套装的发货套数：各「按套计」组件 floor(发货数 / 每套用量) 取最小；全是按组计时退回全部组件 */
export function shipmentSets(lines: FullShipmentLine[]): number | null {
  const withRule = lines.filter((l) => l.orderLine);
  if (!withRule.length) return null;
  const perSet = withRule.filter((l) => modeOf(l.orderLine!) === 'PER_SET');
  const src = perSet.length ? perSet : withRule;
  const each = src.map((l) => {
    const u = unitQuantity(l.orderLine!);
    return u > 0 ? Math.floor(num(l.quantity) / u + 1e-6) : 0;
  });
  return Math.max(0, Math.min(...each));
}

/**
 * 批次明细按套装实例分组。orderLine 来自订单详情的 shipments[].lines；再用订单行补上套装名称和编号
 * （shipments[].lines[].orderLine 是裸订单行，不带 bundle 关联）。
 */
export function groupShipmentLines(sh: FullShipment, orderLines: BundleLine[] = []): ShipLineGroup[] {
  const byId = new Map(orderLines.map((l) => [l.id, l]));
  const out: ShipLineGroup[] = [];
  const bundles = new Map<string, Extract<ShipLineGroup, { kind: 'bundle' }>>();
  for (const raw of sh.lines) {
    const ol = raw.orderLine ? { ...raw.orderLine, bundle: raw.orderLine.bundle ?? byId.get(raw.orderLineId)?.bundle } : byId.get(raw.orderLineId) || null;
    const line: FullShipmentLine = { ...raw, orderLine: ol };
    if (!ol || !isBundleLine(ol)) {
      out.push({ kind: 'single', key: line.id, line });
      continue;
    }
    const key = groupKey(ol, line.orderLineId);
    let g = bundles.get(key);
    if (!g) {
      g = { kind: 'bundle', key, name: bundleName(ol), code: ol.bundle?.code, lines: [], sets: null, ship: 0, loss: 0 };
      bundles.set(key, g);
      out.push(g);
    }
    g.lines.push(line);
    g.ship += num(line.quantity);
    g.loss += num(line.lossQuantity);
  }
  for (const g of bundles.values()) g.sets = shipmentSets(g.lines);
  return out;
}

/** 批次汇总：套装按套、单品按个；破损/补耗与实际出库按个（组件逐个累加） */
export function shipmentTotals(groups: ShipLineGroup[]) {
  let sets = 0;
  let singles = 0;
  let loss = 0;
  let out = 0;
  let unknownSets = false;
  for (const g of groups) {
    if (g.kind === 'single') {
      singles += num(g.line.quantity);
      loss += num(g.line.lossQuantity);
      out += num(g.line.quantity) + num(g.line.lossQuantity);
    } else {
      if (g.sets === null) unknownSets = true;
      sets += g.sets ?? 0;
      loss += g.loss;
      out += g.ship + g.loss;
    }
  }
  return { sets, singles, loss, out, bundles: groups.filter((g) => g.kind === 'bundle').length, unknownSets };
}

/* ------------------------------ 订单进度（套装 1 套记 1） ------------------------------ */

type OrderLineGroup = { bundle: boolean; lines: BundleLine[] };

function groupOrderLines(lines: BundleLine[]): OrderLineGroup[] {
  const map = new Map<string, OrderLineGroup>();
  const out: OrderLineGroup[] = [];
  for (const l of lines) {
    if (!isBundleLine(l)) {
      out.push({ bundle: false, lines: [l] });
      continue;
    }
    const key = groupKey(l, l.id);
    let g = map.get(key);
    if (!g) {
      g = { bundle: true, lines: [] };
      map.set(key, g);
      out.push(g);
    }
    g.lines.push(l);
  }
  return out;
}

/** 套装已发套数：照原系统 getBundleShippedQuantity（按套计组件 floor(已发 / 每套用量) 的最小值，不超过订购套数） */
function bundleShippedSets(g: OrderLineGroup) {
  const ordered = setQuantity(g.lines[0]);
  if (!ordered) return 0;
  const perSet = g.lines.filter((l) => modeOf(l) === 'PER_SET');
  const src = perSet.length ? perSet : g.lines;
  const each = src.map((l) => {
    const u = unitQuantity(l);
    return u > 0 ? Math.floor(num(l.shippedQuantity) / u + 1e-6) : ordered;
  });
  return each.length ? Math.max(0, Math.min(ordered, ...each)) : 0;
}

/**
 * 订单层面的发货进度，单位「套装 1 套记 1、单品 1 个记 1」（与原系统订单页「发货进度」同口径）。
 * thisBatch 由调用方用本批 shipmentTotals 算（补发批次不计入：补发不改订单行已发数）。
 */
export function orderProgress(order: ShipmentOrder) {
  let ordered = 0;
  let shipped = 0;
  for (const g of groupOrderLines(order.lines)) {
    if (g.bundle) {
      ordered += setQuantity(g.lines[0]);
      shipped += bundleShippedSets(g);
    } else {
      ordered += num(g.lines[0].quantity);
      shipped += Math.min(num(g.lines[0].shippedQuantity), num(g.lines[0].quantity));
    }
  }
  return { ordered, shipped, remaining: Math.max(ordered - shipped, 0) };
}
